/**
 * Order external attributes (external ID, status, steps, ...) arrive from the API already split by
 * display position: external_attributes[position] = { slug, name, attributes: [...] }, each attribute as
 * { slug, name, group, multiple, fields: [{ slug, name, type }], entries: [{ [field.slug]: value }] }.
 * Which attributes exist and where they are shown is configured per client in the CMS params, and
 * any attribute - single or multiple - can be placed on any position.
 */

/** Format one field value for display, by the field type defined in the CMS. */
export const formatExternalValue = (field, value) => {
    if (value == null || value === "") {
        return "";
    }

    switch (field?.type) {
        case "datetime": {
            const date = new Date(String(value).replace(" ", "T"));
            if (isNaN(date)) {
                return value;
            }
            const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
            return date.toLocaleString("sr-RS", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                ...(hasTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
            });
        }
        case "boolean":
            return value === "1" || value === 1 || value === true ? "Da" : "Ne";
        default:
            return value;
    }
};

/** All non-empty values of one entry, in field order. */
export const externalEntryValues = (attribute, entry) =>
    (attribute?.fields ?? []).map((field) => formatExternalValue(field, entry?.[field.slug])).filter((value) => value !== "");

/** One attribute as a single text - entries joined with ", ", the values of an entry with " - ". */
export const externalAttributeText = (attribute) =>
    (attribute?.entries ?? [])
        .map((entry) => externalEntryValues(attribute, entry).join(" - "))
        .filter((text) => text !== "")
        .join(", ");

/**
 * [label, value] rows of one attribute for a ListBlock list. A single attribute is one row (its name
 * and value); a multiple attribute (e.g. steps) is its name followed by one row per entry.
 */
export const externalAttributeRows = (attribute) => {
    if (!attribute?.multiple) {
        return [[attribute?.name, externalAttributeText(attribute)]];
    }

    return [
        [attribute.name, ""],
        ...(attribute.entries ?? []).map((entry) => {
            const [first, ...rest] = externalEntryValues(attribute, entry);
            return [first, rest.join(" - ")];
        }),
    ];
};

/** [label, value] rows of every attribute placed on one display position. */
export const externalPositionRows = (position) => (position?.attributes ?? []).flatMap(externalAttributeRows);

/**
 * Values of every field placed on one display position, keyed as table columns from the API
 * (items_table_columns): "external:{attribute.slug}__{field.slug}". Entries of a multiple
 * attribute are joined with ", ".
 */
export const externalFieldValues = (position) => {
    const values = {};
    for (const attribute of position?.attributes ?? []) {
        for (const field of attribute?.fields ?? []) {
            values[`external:${attribute.slug}__${field.slug}`] = (attribute?.entries ?? [])
                .map((entry) => formatExternalValue(field, entry?.[field.slug]))
                .filter((value) => value !== "")
                .join(", ");
        }
    }
    return values;
};
